import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    SOLUTIONS_BY_INDUSTRY,
    SOLUTIONS_BY_CAPABILITY,
    getProductBySlug,
    SolutionItem,
} from "../../data/productsData";
import { loadSolutions } from "../../services/platformSource";
import "../../styles/revamp.css";

/**
 * Solutions, cut two ways.
 *
 * A buyer arrives knowing either their industry or their problem, rarely a
 * product name. Each card names the products that serve it, carrying their
 * signal colours through, so the two mental models resolve to the same catalog.
 */

type Cut = "industry" | "capability";

const SolutionsSection: React.FC = () => {
    const [cut, setCut] = useState<Cut>("industry");
    // Seeded from the static registry so the section paints immediately, then
    // replaced by CMS content when it arrives.
    const [sets, setSets] = useState<{ industry: SolutionItem[]; capability: SolutionItem[] }>({
        industry: SOLUTIONS_BY_INDUSTRY,
        capability: SOLUTIONS_BY_CAPABILITY,
    });

    useEffect(() => {
        let cancelled = false;
        loadSolutions().then((next) => {
            if (!cancelled) setSets(next);
        });
        return () => {
            cancelled = true;
        };
    }, []);

    const CUTS: { key: Cut; label: string; items: SolutionItem[] }[] = [
        { key: "industry", label: "By industry", items: sets.industry },
        { key: "capability", label: "By capability", items: sets.capability },
    ];

    const items = CUTS.find((c) => c.key === cut)!.items;

    return (
        <section className="mx-solutions">
            <div className="container">
                <div className="mx-solutions-head">
                    <div>
                        <p className="mx-eyebrow">Solutions</p>
                        <h2 className="mx-suite-title">Start from where you are.</h2>
                    </div>

                    <div className="mx-segmented" role="tablist" aria-label="Solution view">
                        {CUTS.map((c) => (
                            <button
                                key={c.key}
                                type="button"
                                role="tab"
                                aria-selected={cut === c.key}
                                className="mx-segmented-btn"
                                data-active={cut === c.key}
                                onClick={() => setCut(c.key)}
                            >
                                {c.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="mx-solutions-grid">
                    {items.map((item) => (
                        <article className="mx-card" key={item.slug}>
                            <h3 className="mx-card-title">{item.title}</h3>
                            <p className="mx-card-body">{item.description}</p>

                            <ul className="mx-card-products">
                                {item.productSlugs.map((slug) => {
                                    const p = getProductBySlug(slug);
                                    if (!p) return null;
                                    return (
                                        <li key={slug}>
                                            <Link
                                                to={`/products/${p.slug}`}
                                                style={{ ["--mx-dot-color" as any]: p.accent }}
                                            >
                                                <span className="mx-dot" />
                                                {p.name}
                                            </Link>
                                        </li>
                                    );
                                })}
                            </ul>
                        </article>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default SolutionsSection;
